/**
 * Shared Axios instance for authenticated API calls.
 *
 * Attaches the FlowRide Bearer access token on every request and recovers from
 * expired-access 401s by refreshing once, then retrying the original call.
 * The refresh itself is single-flighted in {@link refreshAccessToken}.
 */

import axios, {
    type AxiosError,
    type AxiosInstance,
    type InternalAxiosRequestConfig,
} from "axios";
import { refreshAccessToken } from "./auth-api.service";
import {
    clearAuthSession,
    peekAuthState,
    setAccessToken,
} from "./auth-session.helpers";

/**
 * Axios request config extended with a one-shot retry marker.
 *
 * `_authRetry` prevents infinite 401 → refresh → retry loops when the retried
 * request still fails with 401.
 */
interface RetryableRequestConfig extends InternalAxiosRequestConfig {

    /** True after this request has already been retried following a refresh. */
    "_authRetry"?: boolean;
}

/**
 * Obtains a fresh access token via the refresh cookie and persists it.
 *
 * On failure, clears the local auth session so the UI returns to logged-out
 * state rather than holding a stale token.
 *
 * @returns Fresh access token string.
 * @throws Re-throws the refresh error after clearing the session.
 */
async function refreshAndPersistAccessToken(): Promise<string> {
    try {
        const response = await refreshAccessToken();
        setAccessToken( response.access_token );

        return response.access_token;
    } catch( error: unknown ) {
        // Refresh failed (expired/revoked cookie, network, etc.): drop local session.
        clearAuthSession();

        throw error;
    }
}

/**
 * Axios client that injects `Authorization: Bearer <accessToken>` and recovers
 * from access-token 401s via a single refresh + retry.
 *
 * Prefer this instance for endpoints that require a FlowRide session.
 * Auth bootstrap endpoints (`/api/auth/login`, `/api/auth/refresh`) should not
 * go through the reactive refresh path; see the response interceptor guard.
 */
export const authHttp: AxiosInstance = axios.create( {
    // Send cookies (e.g. refresh cookie) when the backend sets them on this origin.
    "withCredentials": true,
    "timeout":         30_000,
} );

authHttp.interceptors.request.use( ( config ) => {
    const { accessToken } = peekAuthState();
    if ( accessToken != null && accessToken !== "" ) {
        config.headers.Authorization = `Bearer ${ accessToken }`;
    }

    return config;
} );

authHttp.interceptors.response.use(
    ( response ) => {
        return response;
    },
    async( error: AxiosError ) => {
        const originalRequest = error.config as RetryableRequestConfig | undefined;

        // Only react to first-time 401s on a known request; everything else propagates.
        if (
            error.response?.status !== 401 ||
            originalRequest == null ||
            originalRequest._authRetry === true
        ) {
            throw error;
        }

        const url = originalRequest.url ?? "";

        /*
         * Do not attempt recovery on auth endpoints themselves: a 401 from login
         * or refresh must surface to the caller, not trigger another refresh loop.
         */
        if ( url.includes( "/api/auth/refresh" ) || url.includes( "/api/auth/login" ) ) {
            throw error;
        }

        originalRequest._authRetry = true;
        const accessToken = await refreshAndPersistAccessToken();
        originalRequest.headers.Authorization = `Bearer ${ accessToken }`;

        return authHttp.request( originalRequest );
    },
);
